/**
 * What the Ask dock is about, and whether its chat is open.
 *
 * The dock is the one @yagni chat. Its scope is the center: by default the
 * workspace, and any surface that knows better binds its own while it is
 * mounted (`useDockBinding`): a Team page binds the Team, a ticket open on
 * Work binds the ticket and its comment thread, the decision ledger binds the
 * decision. The last surface mounted wins; unmounting it hands the dock back.
 * A surface's place is set when it mounts, so the page behind an open ticket
 * re-rendering (a refreshed list, a loaded name) never takes the dock from it.
 */
import { useCallback, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import type { PlanQuestionRef } from "../lib/planAnswerBridge.js";
import type { YagniRailScope } from "./yagniRailScopes.js";

/** A one-shot request to send `text` into the dock's thread, deduped by `id`. */
export interface AskSeed {
  id: number;
  text: string;
  /**
   * The plan question the seed asks, when it came from one. The dock then
   * offers each @yagni reply back to that question ("Use as answer").
   */
  planQuestion?: PlanQuestionRef;
}

export interface DockBinding {
  scope: YagniRailScope;
  /** The context chip in the Ask bar, e.g. the open ticket's key. */
  context?: string;
  /** Increment `id` to send `text` into the thread; the dock opens for it. */
  seedRequest?: AskSeed | null;
  onSeedSent?: () => void;
  /** Every tool result in the bound thread, so the surface can follow what @yagni did. */
  onToolResult?: (tool: string, result: unknown) => void;
  /** A second tab beside the conversation (a ticket's comment thread). */
  comments?: ReactNode;
}

/**
 * Words a surface puts in the Ask bar for the person to finish ("On COREX-2131,
 * request changes: "). Never sent on its own: the person sends it.
 */
export interface AskDraft {
  id: number;
  text: string;
  /** The plan question the draft asks about: @yagni's replies can be used as its answer. */
  planQuestion?: PlanQuestionRef;
}

interface DockState {
  open: boolean;
  binding: DockBinding | null;
  draft: AskDraft | null;
}

let state: DockState = { open: false, binding: null, draft: null };
let draftSeq = 0;
/** Every mounted surface in mount order, with its binding (null while it has none). */
let surfaces: { token: symbol; binding: DockBinding | null }[] = [];
const listeners = new Set<() => void>();

function set(next: DockState) {
  if (next.open === state.open && next.binding === state.binding && next.draft === state.draft) return;
  state = next;
  for (const listener of listeners) listener();
}

/** The dock follows the last mounted surface that has a binding. */
function follow() {
  const top = [...surfaces].reverse().find((surface) => surface.binding != null);
  set({ ...state, binding: top?.binding ?? null });
}

export const askDockStore = {
  getSnapshot: (): DockState => state,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  setOpen: (open: boolean) => set({ ...state, open }),
  /** A surface takes its place on mount, above every surface mounted before it. */
  mount: (token: symbol) => {
    if (!surfaces.some((surface) => surface.token === token)) surfaces.push({ token, binding: null });
  },
  /**
   * Set a surface's binding where it stands. `useDockBinding` always mounts
   * first; a caller that binds without mounting (a test standing in for a
   * surface) goes on top.
   */
  bind: (token: symbol, binding: DockBinding | null) => {
    const surface = surfaces.find((entry) => entry.token === token);
    if (surface) surface.binding = binding;
    else surfaces.push({ token, binding });
    follow();
  },
  /** Move a surface on top with its binding, so the dock is its own again. */
  claim: (token: symbol, binding: DockBinding) => {
    surfaces = [...surfaces.filter((surface) => surface.token !== token), { token, binding }];
    follow();
  },
  unbind: (token: symbol) => {
    surfaces = surfaces.filter((surface) => surface.token !== token);
    follow();
  },
  /** Put words in the Ask bar for the person to finish, and open the dock on them. */
  prefill: (text: string, planQuestion?: PlanQuestionRef) =>
    set({ ...state, open: true, draft: { id: ++draftSeq, text, ...(planQuestion ? { planQuestion } : {}) } }),
  /** The bar took the draft. */
  clearDraft: () => set({ ...state, draft: null }),
};

/** Test-only: fold the dock and drop any binding between tests. */
export function __resetAskDockForTests(): void {
  surfaces = [];
  state = { open: false, binding: null, draft: null };
  listeners.clear();
}

export function useAskDock(): DockState {
  return useSyncExternalStore(askDockStore.subscribe, askDockStore.getSnapshot, askDockStore.getSnapshot);
}

/**
 * Bind the dock to this surface's center while it is mounted; null leaves it
 * to the surfaces below. The last surface mounted owns the dock, however often
 * the ones below it re-render. Returns `claim`, which moves this surface back
 * on top: a sheet asking on its own record claims before it opens, in case a
 * surface mounted above it since.
 */
export function useDockBinding(binding: DockBinding | null): () => void {
  const token = useRef(Symbol("dock-binding")).current;
  const latest = useRef(binding);
  latest.current = binding;
  // Declared first, so the place is taken before the first binding is set.
  useLayoutEffect(() => {
    askDockStore.mount(token);
    return () => askDockStore.unbind(token);
  }, [token]);
  useLayoutEffect(() => {
    askDockStore.bind(token, binding);
  });
  return useCallback(() => {
    if (latest.current) askDockStore.claim(token, latest.current);
  }, [token]);
}
